import React from 'react';
import { Checkbox as AntCheckbox, Form } from 'antd';
import type { CheckboxConfig, ValueChangeEvent } from '../types';
import { getCheckboxOptions } from '../types';

interface CheckboxProps {
  config: CheckboxConfig;
  value?: any;
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const Checkbox: React.FC<CheckboxProps> = ({
  config,
  value,
  onChange,
  pageId,
  error
}) => {
  // Extract checkbox-specific options
  const checkboxOptions = getCheckboxOptions(config);
  
  const handleChange = (checked: boolean) => {
    const newValue = checked ? 
      (checkboxOptions.checkedValue !== undefined ? checkboxOptions.checkedValue : true) : 
      (checkboxOptions.uncheckedValue !== undefined ? checkboxOptions.uncheckedValue : false);
    
    onChange({
      componentId: config.id,
      value: newValue,
      pageId
    });
  };

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        validator: (_: any, value: any) => {
          const checkedValue = checkboxOptions.checkedValue !== undefined ? checkboxOptions.checkedValue : true;
          if (value === checkedValue) {
            return Promise.resolve();
          }
          return Promise.reject(new Error(`${config.label} must be checked`));
        }
      });
    }
    
    // Add custom validation rules
    config.validation?.forEach(rule => {
      switch (rule.type) {
        case 'custom':
          rules.push({
            validator: rule.value,
            message: rule.message
          });
          break;
        // Add more validation types as needed
      }
    });
    
    return rules;
  };

  const isChecked = (): boolean => {
    const currentValue = value !== undefined ? value : config.defaultValue;
    const checkedValue = checkboxOptions.checkedValue !== undefined ? checkboxOptions.checkedValue : true;
    return currentValue === checkedValue;
  };

  const formItemProps = {
    required: config.required,
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : config.description,
    rules: validateRules(),
    valuePropName: 'checked'
  };

  return (
    <Form.Item {...formItemProps}>
      <AntCheckbox
        checked={isChecked()}
        onChange={(e) => handleChange(e.target.checked)}
        disabled={config.disabled}
      >
        {config.label}
      </AntCheckbox>
    </Form.Item>
  );
}; 